import { DownloadAttachment } from "./DownloadAttachment";
import { Paperclip } from "lucide-react";

import type { AttachmentNote } from "../types";

/**
 * Displays persisted note attachments in note show/list views.
 *
 * This component receives a full note record and renders all attachments.
 *
 * @param props.note - Note record containing attachments to render.
 * @returns `null` when there are no attachments, otherwise attachment previews and links.
 */
export const NoteAttachments = ({
  note,
}: {
  note: { attachments?: AttachmentNote[] | null };
}) => {
  if (!note.attachments || note.attachments.length === 0) {
    return null;
  }

  const imageAttachments = note.attachments.filter(
    (attachment: AttachmentNote) => isImageMimeType(attachment.type),
  );
  const otherAttachments = note.attachments.filter(
    (attachment: AttachmentNote) => !isImageMimeType(attachment.type),
  );

  return (
    <div className="mt-2 flex flex-col gap-2">
      {imageAttachments.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {imageAttachments.map((attachment: AttachmentNote, index: number) => (
            <div key={index}>
              <DownloadAttachment
                attachment={attachment}
                className="min-w-0 break-all underline hover:no-underline"
              >
                <img
                  src={attachment.src}
                  alt={attachment.title}
                  className="w-full h-[100px] object-cover cursor-pointer object-left border border-border"
                />
              </DownloadAttachment>
            </div>
          ))}
        </div>
      )}
      {otherAttachments.length > 0 &&
        otherAttachments.map((attachment: AttachmentNote, index: number) => (
          <div key={index} className="flex min-w-0 items-start gap-2">
            <Paperclip className="w-4 h-4 shrink-0 mt-1" />
            <DownloadAttachment
              attachment={attachment}
              className="min-w-0 break-all underline hover:no-underline"
            >
              {attachment.title}
            </DownloadAttachment>
          </div>
        ))}
    </div>
  );
};

/**
 * Checks whether a mime type corresponds to an image.
 *
 * @param mimeType - The attachment mime type.
 * @returns `true` when the mime type starts with `image/`.
 */
const isImageMimeType = (mimeType?: string): boolean => {
  if (!mimeType) {
    return false;
  }
  return mimeType.startsWith("image/");
};
